import { useEffect, useRef } from 'react';
import { useUnit } from 'effector-react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { $transferReasonsStore } from '@lib/store';
import { AmountUtils, ValidateUtils } from '@shared/utils';
import type { RecipientResponse } from '@shared/api/rest/recipients';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { IFormDataBusinessCreate } from '../../../payments/subPages/recipientEditor/subPages/businessRecipient';
import { IFormDataBusinessBetween, IFormDataIndividualBetween } from '../betweenAccount';
import { IFormDataBusinessOther, IFormDataIndividualOther } from '../otherTransfer';
import { IFormDataIndividualCreate } from '../../../payments/subPages/recipientEditor/subPages/individualRecipient';
import { FormSchema } from '../betweenAccount/ownAccountTab';
import { FormSchemaCard } from '../cardSettlementPage';
import { ExchangeRate } from '@shared/widgets/exchangeRate';
import { Select } from '@shared/widgets/select';
import { BaseInput, Checkbox, ErrorMessage, Text } from '@shared/widgets';

export enum FastPaymentCountryEnum {
  US = 'US',
}

export enum FastPaymentCurrencyEnum {
  USD = 'USD',
}

export type PaymentFormDataType =
  | IFormDataBusinessCreate
  | IFormDataBusinessBetween
  | IFormDataBusinessOther
  | IFormDataIndividualBetween
  | IFormDataIndividualCreate
  | IFormDataIndividualOther
  | FormSchema
  | FormSchemaCard;

interface PropsType {
  formControl: UseFormReturn<PaymentFormDataType, undefined>;
  handleRequestFee: () => Promise<void>;
  feeData: number | null;
  feeLoading: boolean;
  amount?: number;
  totalAmount: number;
  currency?: string;
  recipient?: RecipientResponse | null;
  reasonOptions?: {
    label: string;
    value: string;
  }[];
  account?: AccountResponse | null;
  exchangeRate: {
    data: number | null;
    loading: boolean;
    error: string | null;
  };
  accountTo?: AccountResponse | null;
}

export const PaymentFormBlock = (props: PropsType) => {
  const transferReasonsStore = useUnit($transferReasonsStore.store);

  const amountVal = props.formControl.watch('amount');
  const beneficiaryAmountVal = props.formControl.watch('beneficiaryAmount');
  const currency = props.formControl.watch('currency');
  const bankCountry = props.formControl.watch('bankCountry');

  const isExchangeRateChanged = useRef(false);

  useEffect(() => {
    props.formControl.setValue('fastPayment', true);
  }, [currency, bankCountry, props.recipient, props.account]);

  useEffect(() => {
    if (!props.exchangeRate.data || amountVal == null || amountVal <= 0) return;

    isExchangeRateChanged.current = true;
    const newBeneficiaryAmount = amountVal * props.exchangeRate.data;
    props.formControl.setValue('beneficiaryAmount', parseFloat(newBeneficiaryAmount.toFixed(2)), {
      shouldValidate: true,
    });
  }, [props.exchangeRate.data]);

  useEffect(() => {
    if (!props.exchangeRate.data || amountVal == null) return;

    if (isExchangeRateChanged.current) {
      isExchangeRateChanged.current = false;
      return;
    }

    const newBeneficiaryAmount = amountVal * props.exchangeRate.data;
    props.formControl.setValue('beneficiaryAmount', parseFloat(newBeneficiaryAmount.toFixed(2)), {
      shouldValidate: true,
    });
  }, [amountVal]);

  useEffect(() => {
    if (beneficiaryAmountVal == null) return;

    if (!props.exchangeRate.data || props.exchangeRate.data === 0) {
      props.formControl.setValue('amount', parseFloat(beneficiaryAmountVal.toFixed(2)), {
        shouldValidate: true,
      });
      return;
    }

    const newAmount = beneficiaryAmountVal / props.exchangeRate.data;
    props.formControl.setValue('amount', parseFloat(newAmount.toFixed(2)), {
      shouldValidate: true,
    });
  }, [beneficiaryAmountVal]);
  return (
    <div className='dark:bg-soft-black bg-white mt-6 p-6'>
      <Text>Payment</Text>
      <Text className='mt-2 mb-1' size='sm'>
        Amount
      </Text>
      <Controller
        name='amount'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          max: { value: 999999999.99, message: 'The amount must be less than or equal to 999,999,999.99' },
          min: { value: 0.01, message: 'The amount cannot be 0. ' },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <div className='relative'>
              <BaseInput
                className='my-2'
                type='number'
                placeholder='Amount'
                required
                onlyPositive
                value={(value as unknown as string) ?? undefined}
                onChange={(value) => {
                  if (typeof value !== 'string') return;
                  if (value.length === 10) return;

                  const correctedValue = value.replace(/^(\d+)(\.\d{0,2})?\d*$/, (match, p1, p2) => {
                    if (p2) {
                      return p1 + p2.padEnd(3, '0').slice(0, 3);
                    } else {
                      return p1;
                    }
                  });
                  if (correctedValue !== '') {
                    const floatValue = parseFloat(correctedValue.replace(',', '.'));
                    onChange(floatValue);
                  } else {
                    onChange(undefined);
                  }
                }}
                onBlur={props.handleRequestFee}
              />
              {value! > 0 && (
                <span className='font-light text-sm opacity-50 absolute top-2 left-[120px]'>
                  {props.feeData !== undefined && props.feeData !== null && !props.feeLoading
                    ? `+ ${AmountUtils.transformToAmountWithCurrency(
                        props.feeData || 0,

                        // recipient ? recipient.currency : currency
                      )}

                      Fee`
                    : 'Fee is calculating'}
                </span>
              )}
            </div>
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Text className='mt-2 mb-1' size='sm'>
        Description
      </Text>
      <Controller
        name='paymentReason'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          maxLength: { value: 100, message: 'Maximum length is 100 characters' },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <BaseInput className='my-2' placeholder='Description' required value={value} onChange={onChange} />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Text className='mt-2 mb-1' size='sm'>
        Purpose
      </Text>
      <Controller
        name='purpose'
        control={props.formControl.control}
        rules={{ required: ValidateUtils.requiredErrorMessage }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <Select
              wrapperClassName='overflow-hidden'
              buttonClassName='bg-transparent'
              iconClassName='!text-primary dark:!text-white'
              items={props.reasonOptions!}
              nullValue={{ isEnable: true, text: 'Purpose' }}
              value={value}
              required
              isLoading={transferReasonsStore.loading}
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </>
        )}
      />
      <Text className='mt-2 mb-1' size='sm'>
        Beneficiary amount
      </Text>
      <Controller
        name='beneficiaryAmount'
        control={props.formControl.control}
        rules={{
          required: ValidateUtils.requiredErrorMessage,
          maxLength: 100,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <div className='relative'>
              <BaseInput
                type='number'
                className='my-2'
                placeholder='Beneficiary Amount'
                value={(value as unknown as string) ?? undefined}
                onChange={(value) => {
                  if (typeof value !== 'string') return;

                  if (value.startsWith('-')) return;

                  const correctedValue = value.replace(/^(\d+)(\.\d{0,2})?\d*$/, (match, p1, p2) => {
                    if (p2) {
                      return p1 + p2.padEnd(3, '0').slice(0, 3);
                    } else {
                      return p1;
                    }
                  });
                  if (correctedValue !== '') {
                    const floatValue = parseFloat(correctedValue.replace(',', '.'));
                    onChange(floatValue);
                  } else {
                    onChange(undefined);
                  }
                }}
              />
            </div>
            <ErrorMessage text={error?.message} />
          </>
        )}
      />

      {(props.recipient
        ? props.recipient?.currency === FastPaymentCurrencyEnum.USD && props?.recipient?.bankCountryCode === FastPaymentCountryEnum.US
        : currency === FastPaymentCurrencyEnum.USD && bankCountry === FastPaymentCountryEnum.US) && (
        <Controller
          name='fastPayment'
          control={props.formControl.control}
          render={({ field: { value, onChange } }) => (
            <div className='flex flex-col my-4'>
              <Checkbox labelClassName='!font-semibold !text-soft-black' label='Fast Payment' readOnly value={value} onChange={onChange} />
            </div>
          )}
        />
      )}
      <div className='max-w-72 w-full mt-2'>
        {props.feeData !== undefined && props.feeData !== null && !props.feeLoading && amountVal! > 0 && (
          <>
            <div className='font-normal t-sm text-slate dark:text-white opacity-30'>Fee</div>
            <div className='font-semibold text-sm text-soft-black dark:text-white'>
              {AmountUtils.transformToAmountWithCurrency(props.feeData, props.account ? props.account.currency : props.currency)}
            </div>
          </>
        )}
      </div>
      {props.exchangeRate.error ? null : (
        <>
          {(props.recipient?.currency ||
            props.accountTo?.currency ||
            (props.account?.currency !== undefined && currency !== undefined && bankCountry !== undefined)) &&
            props.exchangeRate.data && (
              <div className='max-w-72 w-full mt-2'>
                <div className='font-normal t-sm text-slate dark:text-white opacity-30'>Exchange rate</div>
                {props.exchangeRate.loading ? (
                  <div className='font-semibold text-sm text-soft-black dark:text-white'> Calculating...</div>
                ) : (
                  <div className='font-semibold text-sm text-soft-black dark:text-white flex items-center gap-2'>
                    {AmountUtils.transformToAmountWithCurrency(1, props.account?.currency)} ={' '}
                    {AmountUtils.transformToAmountWithCurrency(
                      props.exchangeRate.data,
                      props.accountTo?.currency || props.recipient?.currency || currency || props?.account?.currency,
                    )}
                    <ExchangeRate />
                  </div>
                )}
              </div>
            )}
        </>
      )}
    </div>
  );
};
